import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { ArrowRight, Clock } from 'lucide-react';
import { insights, insightCategories, getInsightsByCategory, getFeaturedInsights } from '../data/insights';
import { useLanguage } from '../context/LanguageContext';

const Insights = () => {
  const { t, language } = useLanguage();
  const [searchParams] = useSearchParams();
  const categoryFilter = searchParams.get('category');
  const [activeCategory, setActiveCategory] = useState(categoryFilter || 'all');

  // Category translations
  const categoryTranslations = {
    'Market Analysis': language === 'es' ? 'Análisis de Mercado' : 'Market Analysis',
    'Relocation': language === 'es' ? 'Reubicación' : 'Relocation',
    'Legal & Tax': language === 'es' ? 'Legal e Impuestos' : 'Legal & Tax',
    'Investment': language === 'es' ? 'Inversión' : 'Investment'
  };

  // Update SEO
  useEffect(() => {
    const baseUrl = 'https://mundalo.com';
    const pageUrl = `${baseUrl}/insights`;
    
    document.title = language === 'es' ? 'Perspectivas | Mundalo' : 'Insights | Mundalo';
    
    // Helper to set or create meta tag
    const setMetaTag = (selector, attribute, value) => {
      let tag = document.querySelector(selector);
      if (!tag) {
        tag = document.createElement('meta');
        if (selector.includes('property=')) {
          tag.setAttribute('property', selector.match(/property="([^"]+)"/)[1]);
        } else if (selector.includes('name=')) {
          tag.setAttribute('name', selector.match(/name="([^"]+)"/)[1]);
        }
        document.head.appendChild(tag);
      }
      tag.setAttribute(attribute, value);
    };

    // Helper to set or create link tag
    const setLinkTag = (rel, href) => {
      let tag = document.querySelector(`link[rel="${rel}"]`);
      if (!tag) {
        tag = document.createElement('link');
        tag.setAttribute('rel', rel);
        document.head.appendChild(tag);
      }
      tag.setAttribute('href', href);
    };

    // Canonical
    setLinkTag('canonical', pageUrl);

    const description = language === 'es' 
      ? 'Perspectivas estratégicas sobre nearshoring, bienes raíces transfronterizos, desarrollo y reubicación entre EE.UU. y México.'
      : 'Strategic insights on nearshoring, cross-border real estate, development, and relocation between the US and Mexico.';

    // Meta description
    setMetaTag('meta[name="description"]', 'content', description);

    // Open Graph
    setMetaTag('meta[property="og:title"]', 'content', document.title);
    setMetaTag('meta[property="og:description"]', 'content', description);
    setMetaTag('meta[property="og:url"]', 'content', pageUrl);
    setMetaTag('meta[property="og:type"]', 'content', 'website');

    // Twitter
    setMetaTag('meta[name="twitter:card"]', 'content', 'summary_large_image');
    setMetaTag('meta[name="twitter:title"]', 'content', document.title);
    setMetaTag('meta[name="twitter:description"]', 'content', description);
  }, [language]);

  const filteredInsights = activeCategory === 'all' 
    ? insights 
    : getInsightsByCategory(activeCategory);

  const featuredInsight = getFeaturedInsights()[0];

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString(language === 'es' ? 'es-MX' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric', timeZone: 'America/Chicago'});
  };

  return (
    <div className="min-h-screen bg-[#F9F6EF]">
      <SEO
        title="Insights"
        description="Market intelligence, relocation guides, and investment perspectives from The Mundalo. Expert analysis on cross-border real estate in Mexico."
        path="/insights"
      />
      <Header currentPage="insights" />

      {/* Hero */}
      <section className="pt-32 pb-16 border-b border-[#FFFFFF]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <p className="text-[11px] font-medium text-[#555] uppercase tracking-[0.2em] mb-4">
            {language === 'es' ? 'Perspectivas' : 'Perspectives'}
          </p>
          <h1 className="text-4xl lg:text-5xl font-light text-white mb-6">{t('insights.title')}</h1>
          <p className="text-lg text-[#666] max-w-2xl leading-relaxed">
            {t('insights.subtitle')}
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-6 border-b border-[#FFFFFF]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setActiveCategory('all')}
              className={`px-4 py-2 text-sm font-medium transition ${
                activeCategory === 'all'
                  ? 'bg-white text-black'
                  : 'text-[#888] hover:text-white'
              }`}
              data-testid="filter-all"
            >
              {t('insights.categories.all')}
            </button>
            {insightCategories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`px-4 py-2 text-sm font-medium transition ${
                  activeCategory === cat.id
                    ? 'bg-white text-black'
                    : 'text-[#888] hover:text-white'
                }`}
                data-testid={`filter-${cat.id}`}
              >
                {categoryTranslations[cat.name] || cat.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Article (only show on 'all' filter) */}
      {activeCategory === 'all' && featuredInsight && (
        <section className="py-16 lg:py-20 border-b border-[#FFFFFF]">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <Link 
              to={`/insights/${featuredInsight.slug}`}
              className="group grid lg:grid-cols-2 gap-8 lg:gap-12"
              data-testid="featured-article"
            >
              <div className="aspect-[16/10] overflow-hidden bg-[#FFFFFF]">
                <img 
                  src={featuredInsight.heroImage}
                  alt={featuredInsight.title}
                  className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
                />
              </div>
              <div className="flex flex-col justify-center">
                <p className="text-[11px] font-medium text-[#2CA8A0] uppercase tracking-[0.15em] mb-4">
                  {language === 'es' ? 'Destacado' : 'Featured'}
                </p>
                <h2 className="text-2xl lg:text-3xl font-light text-white mb-4 group-hover:text-[#ccc] transition">
                  {featuredInsight.title}
                </h2>
                <p className="text-[#888] leading-relaxed mb-6">
                  {featuredInsight.excerpt}
                </p>
                <div className="flex items-center gap-4 text-sm text-[#666]">
                  <span>{formatDate(featuredInsight.publishedAt)}</span>
                  <span className="w-1 h-1 bg-[#444] rounded-full" />
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {featuredInsight.readTime}
                  </span>
                </div>
              </div>
            </Link>
          </div>
        </section>
      )}

      {/* Article Grid */}
      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {activeCategory !== 'all' && (
            <h2 className="text-2xl font-light text-white mb-10">
              {categoryTranslations[insightCategories.find(c => c.id === activeCategory)?.name] || 
               insightCategories.find(c => c.id === activeCategory)?.name}
            </h2>
          )}

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredInsights
              .filter(insight => activeCategory !== 'all' || insight.id !== featuredInsight?.id)
              .map((insight) => {
                const category = insightCategories.find(c => c.id === insight.category);
                return (
                  <Link
                    key={insight.id}
                    to={`/insights/${insight.slug}`}
                    className="group"
                    data-testid={`article-${insight.id}`}
                  >
                    <div className="aspect-[16/10] overflow-hidden bg-[#FFFFFF] mb-5">
                      <img 
                        src={insight.heroImage}
                        alt={insight.title}
                        className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
                      />
                    </div>
                    <div>
                      <p className="text-[11px] font-medium text-[#555] uppercase tracking-wider mb-2">
                        {categoryTranslations[category?.name] || category?.name}
                      </p>
                      <h3 className="text-lg font-light text-white mb-3 group-hover:text-[#ccc] transition line-clamp-2">
                        {insight.title}
                      </h3>
                      <p className="text-sm text-[#888] mb-4 line-clamp-2">
                        {insight.excerpt}
                      </p>
                      <div className="flex items-center gap-4 text-[12px] text-[#666]">
                        <span>{formatDate(insight.publishedAt)}</span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5" />
                          {insight.readTime}
                        </span>
                      </div>
                    </div>
                  </Link>
                );
              })}
          </div>

          {filteredInsights.length === 0 && (
            <div className="text-center py-16">
              <p className="text-[#666]">
                {language === 'es' ? 'No se encontraron artículos en esta categoría.' : 'No articles found in this category.'}
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-16 bg-[#F9F6EF] border-t border-[#FFFFFF]">
        <div className="max-w-2xl mx-auto px-6 lg:px-12 text-center">
          <p className="text-[11px] font-medium text-[#555] uppercase tracking-[0.2em] mb-4">
            {language === 'es' ? 'Mantente Informado' : 'Stay Informed'}
          </p>
          <h2 className="text-2xl lg:text-3xl font-light text-white mb-4">
            {language === 'es' ? 'Recibe Nuestras Últimas Perspectivas' : 'Get Our Latest Insights'}
          </h2>
          <p className="text-[#666] mb-8">
            {language === 'es' 
              ? 'Suscríbete para recibir análisis estratégico sobre bienes raíces transfronterizos y tendencias del mercado.'
              : 'Subscribe to receive strategic analysis on cross-border real estate and market trends.'}
          </p>
          <Link 
            to="/contact?type=newsletter"
            className="inline-block text-sm font-medium text-white border border-[#333] px-8 py-4 hover:bg-white hover:text-black transition-all"
          >
            {language === 'es' ? 'Suscribirse a Actualizaciones' : 'Subscribe to Updates'}
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Insights;
